/**
 * Pure utility functions for the checkout flow.
 * No React hooks or side effects — safe to test without a DOM.
 */

import { signInAnonymously } from 'firebase/auth'
import { auth, isFirebaseConfigured } from '@/config/firebase'

export const IDEMPOTENCY_SESSION_KEY = 'checkout_idempotency_key'

export function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key) } catch { return null }
}

export function safeSessionSet(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch { /* ignore */ }
}

/**
 * Normalize scheduled pickup values to ISO 8601 datetimes.
 * Accepts ISO strings directly and falls back to legacy "Today, 2:30 PM" format.
 */
export function pickupTimeToISO(pickupTime: string | undefined | null): string | null {
  if (!pickupTime || pickupTime === 'ASAP') return null
  if (!Number.isNaN(Date.parse(pickupTime))) return new Date(pickupTime).toISOString()

  const match = pickupTime.match(/^(Today|Tomorrow),\s*(\d+):(\d+)\s*(AM|PM)$/i)
  if (!match) return null

  const [, dayLabel, hourStr, minStr, period] = match
  let hours = parseInt(hourStr)
  const minutes = parseInt(minStr)

  if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12
  if (period.toUpperCase() === 'AM' && hours === 12) hours = 0

  const date = new Date()
  if (dayLabel === 'Tomorrow') date.setDate(date.getDate() + 1)
  date.setHours(hours, minutes, 0, 0)

  return date.toISOString()
}

/** Extract a readable error message from RTK Query / DRF error responses. */
export function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message

  if (typeof err === 'object' && err !== null) {
    const data = (err as Record<string, unknown>).data
    if (typeof data === 'string') return data
    if (typeof data === 'object' && data !== null) {
      const d = data as Record<string, unknown>
      if (typeof d.error === 'string') return d.error
      if (typeof d.detail === 'string') return d.detail
      const messages = Object.entries(d)
        .map(([key, val]) => {
          const msg = Array.isArray(val) ? val.join(', ') : String(val)
          return `${key}: ${msg}`
        })
        .join('; ')
      if (messages) return messages
    }
  }

  return 'Checkout failed'
}

// ── Checkout types ────────────────────────────────────────────────────────

export type CheckoutStep =
  | 'idle'
  | 'authenticating'
  | 'syncing-cart'
  | 'validating'
  | 'processing'
  | 'awaiting-payment'
  | 'done'
  | 'error'

export interface CheckoutResult {
  orderId: string
  orderNumber: string
  trackingCode: string
  total: number
  currency: string
  clientSecret: string
  status: string
  estimatedReadyAt: string | null
}

export interface CheckoutOptions {
  customerName?: string
  customerEmail?: string
  customerPhone?: string
  notificationChannel?: string
  contactVerified?: boolean
  scheduledPickupAt?: string | null
  tipAmount?: number
  orderType?: string
  menuId?: string
  locationId?: string | null
  notes?: string
  deliveryInfo?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    apartmentNumber?: string
    deliveryInstructions?: string
  } | null
  idempotency_key?: string
}

// ── Auth ──────────────────────────────────────────────────────────────────

export async function ensureAuth(): Promise<void> {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured. Add VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID to .env')
  }
  if (auth.currentUser) return

  try {
    const credential = await signInAnonymously(auth)
    await credential.user.getIdToken()
  } catch (firebaseErr) {
    console.error('[CHECKOUT] Anonymous sign-in FAILED:', firebaseErr)
    throw firebaseErr
  }
}
